import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Palette, 
  Smartphone, 
  Globe, 
  Brain,
  CheckCircle,
  Users,
  Award,
  TrendingUp
} from 'lucide-react';
import './Home.css';

const Home = () => {
  const services = [
    {
      icon: <Palette size={40} />,
      title: 'UI/UX Design',
      description: 'Elevate your digital experience with our innovative and user-centered UI/UX design services. Our expert team combines creativity and functionality to deliver captivating and seamless user experiences.',
      gradient: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)'
    },
    {
      icon: <Smartphone size={40} />,
      title: 'Mobile Development',
      description: 'Take your business to the next level with our exceptional mobile app development services. We create dynamic, user-friendly, and high-performance apps for iOS and Android platforms.',
      gradient: 'linear-gradient(135deg, #FF8C42 0%, #FFB627 100%)'
    },
    {
      icon: <Globe size={40} />,
      title: 'Web Development',
      description: 'Empower your online presence with our state-of-the-art web development services. We build robust, responsive, and scalable websites tailored to your business needs.',
      gradient: 'linear-gradient(135deg, #E85D04 0%, #FF6B35 100%)'
    },
    {
      icon: <Brain size={40} />,
      title: 'AI Solutions',
      description: 'Elevate your business with our ML and AI services. Transform data into actionable insights and predictive models, propelling you to the forefront of innovation and efficiency.',
      gradient: 'linear-gradient(135deg, #F7931E 0%, #FFB627 100%)'
    }
  ];

  const stats = [
    { icon: <Users size={32} />, value: '30', label: 'Happy Clients' },
    { icon: <Award size={32} />, value: '30+', label: 'Projects Completed' },
    { icon: <TrendingUp size={32} />, value: '98%', label: 'Success Rate' },
    { icon: <CheckCircle size={32} />, value: '9+', label: 'Years Experience' }
  ];

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-background">
          <div className="hero-dots"></div>
        </div>
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="hero-title">
              Empowering Your Business<br />
              Through <span className="gradient-text">Innovative IT Solutions</span>
            </h1>
            <p className="hero-description">
              We specialize in creating custom software solutions that enable businesses to optimize their processes and achieve lasting growth.
            </p>
            <div className="hero-buttons">
              <Link to="/contact" className="btn btn-primary">
                Consult Logifox <ArrowRight size={20} />
              </Link>
              <Link to="/services" className="btn btn-outline">
                Our Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="stat-card"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="stat-icon">{stat.icon}</div>
                <h3 className="stat-value">{stat.value}</h3>
                <p className="stat-label">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Our Services</h2>
            <p className="section-subtitle">
              Comprehensive IT solutions designed to transform your business and drive innovation
            </p>
          </motion.div>

          <div className="services-grid">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="service-card"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="service-card-inner">
                  <div className="service-icon" style={{ background: service.gradient }}>
                    {service.icon}
                  </div>
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                  <Link to="/services" className="service-link">
                    View More <ArrowRight size={18} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="cta-title">Ready to Transform Your Business?</h2>
            <p className="cta-description">
              Let's discuss how our innovative solutions can help you achieve your goals
            </p>
            <Link to="/contact" className="btn btn-primary btn-large">
              Get Started Today <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
